import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeftIcon, ClipboardCopyIcon, CheckIcon } from '@heroicons/react/solid';
import { DashHeader, Footer, LoadingOverlay, Signin, Toast } from '../components';
import PromoCodeForm from '../components/PromoCodeForm';
import { useAuth } from '../contexts/AuthContext';

// Refer & earn (GROWTH-002) and account credit history (GROWTH-001). Everything comes from the
// employerCredits function, scoped to the signed-in employer; credit is granted server-side only.

const isoDate = (value) => (value ? String(value).slice(0, 10) : '—'); // exact dates (D-013)
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS = {
  pending: { label: 'Waiting for first paid posting', style: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
  rewarded: { label: 'Rewarded', style: 'bg-green-50 text-green-700 border-green-200' },
};

function creditLabel(entry) {
  if (entry.kind === 'promo') return `Promo code ${entry.code}`;
  if (entry.kind === 'referral') return `Referral reward: ${entry.company || 'a business you referred'} made their first posting`;
  if (entry.kind === 'referral-welcome') return 'Welcome credit for joining with a referral';
  return 'Account credit';
}

function Referrals() {
  const { currentUser, signout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState('');

  function load() {
    return axios
      .post('/.netlify/functions/employerCredits', { action: 'summary' })
      .then((res) => setInfo(res.data.result))
      .catch((err) => {
        if (err.response && err.response.status === 403) setError('Please verify your email address to get your referral code.');
        else if (err.response && err.response.status === 404) setError('Please create your profile first.');
        else setError('We could not load your referrals. Please refresh the page.');
      });
  }

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    load().finally(() => setLoading(false));
  }, [currentUser]);

  function handleSignout() {
    try {
      signout();
      navigate('/');
    } catch (err) {
      console.log(err);
    }
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(info.referralCode);
    } catch (e) {
      return; // the code stays selectable on screen
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  if (loading) return <LoadingOverlay />;
  if (!currentUser) return <Signin />;

  const reward = info ? money(info.rewardAmount) : '$10.00';
  const referrals = (info && info.referrals) || [];
  const pending = referrals.filter((r) => r.status === 'pending').length;
  const rewarded = referrals.filter((r) => r.status === 'rewarded').length;

  return (
    <div className="flex min-h-screen flex-col">
      <DashHeader handle={handleSignout} userEmail={currentUser?.email} />
      <main className="mx-auto w-full max-w-4xl flex-grow px-4 py-8 sm:px-6">
        <Link to="/dashboard" className="inline-flex items-center font-semibold text-gray-900">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          Back to dashboard
        </Link>

        <h1 className="mt-4 text-2xl font-bold font-[Poppins] text-gray-900">Refer &amp; earn</h1>
        <p className="mt-1 text-sm text-gray-600">
          Invite another business to CanadaJobLeads. When they make their first paid posting, you each get {reward} in account credit.
          {info && ` You can earn up to ${money(info.maxEarnings)} (${info.maxRewards} rewarded referrals).`}
        </p>

        {error && <p className="mt-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}

        {info && (
          <>
            {info.referredBy === 'pending' && (
              <p className="mt-6 rounded-md border border-red-100 bg-red-50 px-4 py-3 text-sm text-gray-700">
                You joined with a referral. You and the business that referred you each get {reward} credit after your first paid posting.
              </p>
            )}

            {info.earnedFromReferrals >= info.maxEarnings && (
              <p className="mt-6 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-gray-700">
                You've earned the {money(info.maxEarnings)} referral maximum. Thank you! Businesses you refer still get their {reward} credit.
              </p>
            )}

            {/* Code */}
            <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">Your referral code</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="select-all rounded-md border border-gray-300 bg-gray-50 px-4 py-2 font-mono text-lg font-bold tracking-widest text-gray-900">
                  {info.referralCode}
                </span>
                <button
                  type="button"
                  onClick={copyCode}
                  className="inline-flex cursor-pointer items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-red-800"
                >
                  {copied ? <CheckIcon className="mr-1.5 h-4 w-4" aria-hidden="true" /> : <ClipboardCopyIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />}
                  {copied ? 'Copied' : 'Copy code'}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                The business enters it in the Referral code field when creating their profile on canadajobleads.ca.
              </p>
            </section>

            {/* How it works */}
            <section className="mt-6 grid gap-4 sm:grid-cols-3">
              {[
                ['1', 'Share your code', 'Send it to a business that hires in Canada.'],
                ['2', 'They post a job', `They enter it when creating their profile and make their first posting, paid by card (from ${money(info.minPayment)}).`],
                ['3', `You each get ${reward}`, 'The credit is applied automatically to your next posting, extension or renewal.'],
              ].map(([n, title, text]) => (
                <div key={n} className="rounded-lg border border-gray-200 bg-white p-4">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-sm font-bold text-primary">{n}</span>
                  <p className="mt-2 text-sm font-semibold text-gray-900">{title}</p>
                  <p className="mt-1 text-sm text-gray-600">{text}</p>
                </div>
              ))}
            </section>

            {/* Stats */}
            <section className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                ['Signed up', referrals.length],
                ['Pending', pending],
                ['Rewarded', rewarded],
                ['Credit earned', `${money(info.earnedFromReferrals)} of ${money(info.maxEarnings)}`],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-gray-200 bg-white p-4 text-center">
                  <p className="text-xl font-bold text-gray-900">{value}</p>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
                </div>
              ))}
            </section>

            {/* Activity */}
            <section className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900">Referral activity</h2>
              {referrals.length === 0 ? (
                <p className="mt-2 text-sm text-gray-600">No one has signed up with your code yet.</p>
              ) : (
                <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-2">Business</th>
                        <th className="px-4 py-2">Signed up</th>
                        <th className="px-4 py-2">Status</th>
                        <th className="px-4 py-2 text-right">Your credit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {referrals.map((r, i) => (
                        <tr key={`${r.createdAt}-${i}`}>
                          <td className="px-4 py-2">
                            <p className="font-medium text-gray-900">{r.company || 'New business'}</p>
                            <p className="text-xs text-gray-500">{r.email}</p>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-gray-700">{isoDate(r.createdAt)}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${(STATUS[r.status] || STATUS.pending).style}`}>
                              {(STATUS[r.status] || STATUS.pending).label}
                            </span>
                            {r.rewardedAt && <p className="mt-0.5 text-xs text-gray-500">{isoDate(r.rewardedAt)}</p>}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-right font-medium text-gray-900">{r.earned ? money(r.earned) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* Credit history */}
            <section className="mt-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Account credit</h2>
                <p className="text-sm text-gray-700">
                  Balance: <span className="font-semibold text-primary">{money(info.balance)}</span>
                </p>
              </div>
              {info.creditLog.length === 0 ? (
                <p className="mt-2 text-sm text-gray-600">No credit added yet.</p>
              ) : (
                <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white text-sm">
                  {info.creditLog.map((c, i) => (
                    <li key={`${c.timestamp}-${i}`} className="flex items-start justify-between gap-4 px-4 py-2">
                      <div>
                        <p className="text-gray-900">{creditLabel(c)}</p>
                        <p className="text-xs text-gray-500">{isoDate(c.timestamp)}</p>
                      </div>
                      <span className="whitespace-nowrap font-medium text-green-700">+{money(c.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {info.promoEligible && !info.pendingPromoCode && (
                <div className="mt-3">
                  <PromoCodeForm
                    onApplied={(r) => {
                      setToast(`Promo code ${r.code} applied: ${money(r.credit)} credit added to your account.`);
                      load();
                    }}
                  />
                </div>
              )}
            </section>

            <p className="mt-8 text-xs text-gray-500">
              Account credit has no cash value. It can't be withdrawn, transferred or refunded, and is used only for postings, extensions and renewals on
              CanadaJobLeads. A referral counts once per new business. You can earn referral credit for up to {info.maxRewards} businesses ({money(info.maxEarnings)} in total). Self-referrals and duplicate accounts don't qualify. See the{' '}
              <Link to="/terms" className="underline hover:text-gray-700">
                Terms
              </Link>
              .
            </p>
          </>
        )}
      </main>
      <Toast isOpen={Boolean(toast)} onClose={() => setToast('')} message={toast} type="success" />
      <Footer />
    </div>
  );
}

export default Referrals;
